/**
 * Get translated text for a language from a translations array [{ text, language }].
 * @param {Array} translations - Array of { text, language } objects
 * @param {string|number} langId - Language id to match
 * @returns {string}
 */
export const getTranslation = (translations, langId) => {
  if (!translations || !Array.isArray(translations)) return '';
  const langIdStr = langId?.toString?.() || langId;
  for (let i = 0; i < translations.length; i++) {
    const lang = translations[i]?.language;
    const match = lang && (lang._id?.toString?.() === langIdStr || lang === langIdStr || (typeof lang === 'object' && lang._id === langIdStr));
    if (match) {
      const text = translations[i].text;
      if (text && typeof text === 'string') return text;
      return '';
    }
  }
  // Fallback: return first available text if language not matched
  if (translations.length > 0 && translations[0]?.text) {
    const text = translations[0].text;
    if (text && typeof text === 'string') return text;
  }
  return '';
};

/**
 * Return true if this item's language matches langId (language may be object with _id or string id).
 */
function matchesLanguageId(item, langIdStr) {
  const lang = item?.language;
  if (!lang) return false;
  if (lang._id?.toString?.() === langIdStr || lang === langIdStr) return true;
  if (typeof lang === 'object' && lang._id != null) return lang._id.toString() === langIdStr;
  return false;
}

/**
 * Get array of translated strings from array of translation objects.
 * Uses same language matching as getTranslation so string or object language ids work.
 * @param {Array} translations - Array of { text, language } (language may be id string or { _id })
 * @param {string|number} langId - Language id to match
 * @returns {string[]}
 */
export const getTranslationArr = (translations, langId) => {
  const arr = [];
  if (!translations || !Array.isArray(translations)) return arr;
  const langIdStr = langId?.toString?.() || langId;
  translations.forEach((con) => {
    if (matchesLanguageId(con, langIdStr) && con?.text != null && con.text !== '') {
      arr.push(typeof con.text === 'string' ? con.text : String(con.text));
    }
  });
  // Fallback: if no language match (e.g. API sends different id shape), use all non-empty texts so data isn't lost
  if (arr.length === 0) {
    translations.forEach((con) => {
      const t = con?.text;
      if (t != null && t !== '') arr.push(typeof t === 'string' ? t : String(t));
    });
  }
  return arr;
};

/**
 * Get translated title from an item (e.g. offence, org) that may have title as array, string, or { translations }.
 * @param {object} item - Object with .title (array, string, or { translations }) or .text
 * @param {string|number} langId - Language id for translation lookup
 * @returns {string}
 */
export const getTranslatedItemTitle = (item, langId) => {
  if (!item) return '';
  const title = item.title;
  if (Array.isArray(title)) {
    const t = getTranslation(title, langId);
    if (t) return t;
    if (title[0]?.text && typeof title[0].text === 'string') return title[0].text;
    return '';
  }
  if (typeof title === 'string') return title;
  if (title?.translations && Array.isArray(title.translations)) {
    const t = getTranslation(title.translations, langId);
    if (t) return t;
    return title?.text || '';
  }
  if (title?.text && typeof title.text === 'string') return title.text;
  if (item.text) return item.text;
  return '';
};

/**
 * Get title from appContent by name key.
 * Handles both processed (title = string) and raw API (title = [{ text, language }]) shapes.
 * @param {Array} appContent - The app content array from Redux
 * @param {string} name - The name key to look up (e.g., "Emergency Services")
 * @param {string|number} [langId] - Optional language id when title is raw array; uses first text if omitted
 * @returns {string} The translated title text or empty string
 */
export const getTitleFromAppContent = (appContent, name, langId) => {
  if (!appContent || !Array.isArray(appContent)) return '';
  for (let i = 0; i < appContent.length; i++) {
    if (appContent[i].name === name) {
      const title = appContent[i].title;
      if (typeof title === 'string') return title;
      if (Array.isArray(title)) {
        if (langId != null) return getTranslation(title, langId);
        if (title.length > 0 && title[0]?.text != null) return typeof title[0].text === 'string' ? title[0].text : '';
        return '';
      }
      if (typeof title === 'object' && title !== null) {
        if (typeof title.text === 'object' && title.text !== null) return title.text.text || '';
        return title.text !== undefined ? title.text : '';
      }
      return '';
    }
  }
  return '';
};

/**
 * Log error in development only. Use in Redux thunks for consistent error reporting.
 * @param {Error|*} err
 * @param {string} [context] - e.g. slice name or action type
 */
export const logError = (err, context = "") => {
  if (process.env.NODE_ENV === "development") {
    console.error(context ? `[${context}]` : "", err);
  }
};

/**
 * Get content title with support for multiple name variations
 * This is useful when a content name was renamed (e.g., "Referral" -> "Support Providing Organizations")
 * @param {Array} appContent - The app content array from Redux
 * @param {Array<string>} names - Array of name keys to try in order
 * @returns {string} The first found translated title or empty string
 */
export const getContentTitle = (appContent, names) => {
  if (!appContent || !Array.isArray(appContent)) return '';
  if (!names || !Array.isArray(names)) return '';
  
  for (const name of names) {
    const title = getTitleFromAppContent(appContent, name);
    if (title) return title;
  }
  return '';
};
